// @ts-nocheck
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  fmtDateTime,
  getLatestAppForJob,
  candidateHasAnyInterviewTranscript,
  activeRoleHasTranscript,
  interviewStartSlotStatus,
  SB,
} from "@/legacy/helpersModule";
import { Badge } from "@/shared/components/ui/Badge";
import {
  fetchCandidateStats,
  fetchCandidatesPage,
  exportCandidateReport,
  exportCandidateStatusReport,
} from "@/shared/api/candidatesApi";
import { fetchTalentPoolStats } from "@/shared/api/talentPoolApi";
import { Button, Input, Skeleton, useToast } from "@/shared/components/ui";
import { CvsStatsModal } from "@/features/hr/components/CvsStatsModal";

const PAGE_SIZE = 50;

// Persist the dashboard's navigation state (filters + pagination, including the
// cursor chain that cursor-based tabs need) so that opening a candidate and
// coming Back restores the exact same page/filter — instead of snapping to
// page 1. sessionStorage is used so it also survives a refresh but is scoped to
// the tab session; it is cleared on logout. Storing the cursorStack is what
// lets cursor-paginated tabs (ALL / Registered / in-process) land on the right
// page, which a bare ?page= URL param could not do.
const DASH_STATE_KEY = "hrDashNavState";
function readDashSnapshot() {
  try {
    return JSON.parse(sessionStorage.getItem(DASH_STATE_KEY) || "null");
  } catch (_) {
    return null;
  }
}

// Resolve the dashboard's initial state from (in priority order) the URL query
// params — so shareable/deep-linkable /hr?status=…&job=… links work — then the
// sessionStorage snapshot, then defaults. Filters come from the URL when it has
// any of them; page + cursor chain come from the snapshot ONLY when its filters
// exactly match the resolved filters (otherwise a deep-linked/changed filter
// must start at page 1, since a stale cursor chain would point at the wrong
// data). The cursor chain lives only in the snapshot, never the URL.
function computeInitialDashState(searchParams, snap) {
  const urlStatus = searchParams.get("status");
  const urlScope = searchParams.get("scope");
  const urlJob = searchParams.get("job");
  const urlQ = searchParams.get("q");
  const hasUrl =
    urlStatus != null || urlScope != null || urlJob != null || urlQ != null;

  let filter;
  let roleScope;
  let jobFilter;
  let search;
  if (hasUrl) {
    filter = urlStatus || "ALL";
    roleScope = urlScope || "all";
    jobFilter = urlJob || "";
    search = urlQ || "";
  } else if (snap) {
    filter = snap.filter ?? "ALL";
    roleScope = snap.roleScope ?? "all";
    jobFilter = snap.jobFilter ?? "";
    search = snap.search ?? "";
  } else {
    filter = "ALL";
    roleScope = "all";
    jobFilter = "";
    search = "";
  }

  const snapMatches =
    !!snap &&
    (snap.filter ?? "ALL") === filter &&
    (snap.roleScope ?? "all") === roleScope &&
    (snap.jobFilter ?? "") === jobFilter &&
    (snap.search ?? "") === search;

  return {
    filter,
    roleScope,
    jobFilter,
    search,
    page: snapMatches ? snap.page ?? 1 : 1,
    cursorStack: snapMatches ? snap.cursorStack ?? [] : [],
    nextCursor: snapMatches ? snap.nextCursor ?? null : null,
    inProcessExpanded: snap?.inProcessExpanded ?? false,
  };
}

function dashboardStatusLabel(status) {
  if (status === "NOT_APPLIED") return "Registered";
  if (status === "REATTMPT") return "Reattempt";
  if (status === "RESCHEDULED") return "Rescheduled";
  if (status === "INCOMPLETE") return "Incomplete";
  if (status === "MISSED") return "Missed";
  if (status === "SCHEDULED") return "Scheduled";
  if (status === "POTENTIAL") return "Potential";
  return status || "—";
}

function roleStatusBadgeClass(status) {
  if (status === "REJECTED") return "bg-red-100 text-red-600";
  if (status === "POTENTIAL") return "bg-indigo-100 text-indigo-700";
  if (status === "INTERVIEWED") return "bg-teal-100 text-teal-700";
  if (status === "APPLIED") return "bg-amber-100 text-amber-800";
  if (status === "REATTMPT") return "bg-brand-100 text-brand-800";
  if (status === "RESCHEDULED") return "bg-rose-100 text-rose-700";
  if (status === "INCOMPLETE") return "bg-orange-100 text-orange-700";
  if (status === "MISSED") return "bg-slate-200 text-ink-600";
  if (status === "SCHEDULED") return "bg-sky-100 text-sky-700";
  return "bg-blue-100 text-blue-700";
}

/** Stages nested inside the "Interview In Process" umbrella card. */
const IN_PROCESS_STAGES = ["SCHEDULED", "RESCHEDULED", "REATTMPT", "INCOMPLETE", "MISSED"];

/** A single removable active-filter pill shown under the Candidates header. */
function FilterChip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 bg-brand-50 text-brand-700 border border-brand-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs font-semibold max-w-[16rem]">
      <span className="truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        title="Remove filter"
        className="shrink-0 w-4 h-4 flex items-center justify-center rounded-full hover:bg-brand-200 text-brand-700"
      >
        ✕
      </button>
    </span>
  );
}

function DecisionCard({ item, onOpenRole, showRoleStatus = false }) {
  const roles = item.roles || [];
  const [expanded, setExpanded] = useState(roles.length <= 1);
  const badgeClass = roleStatusBadgeClass(item.decisionStatus);
  return (
    <div className="bg-white rounded-2xl border border-line p-5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full flex items-start justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600 font-black text-lg shrink-0">
            {item.name?.[0] || "?"}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-ink-900 truncate" title={item.name}>
              {item.name}
              <span className="text-ink-400 font-semibold">
                {" · "}
                {item.roleCount} {item.roleCount === 1 ? "Role" : "Roles"}
              </span>
            </p>
            <p className="text-xs text-ink-400 truncate" title={item.email}>{item.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className={`text-xs font-black uppercase px-2 py-1 rounded-lg ${badgeClass}`}
          >
            {dashboardStatusLabel(item.decisionStatus)}
          </span>
          <span className="text-ink-400 text-xs">
            {expanded ? "▲" : "▼"}
          </span>
        </div>
      </button>
      {expanded && (
        <ul className="mt-3 pt-3 border-t border-line space-y-1">
          {roles.map((r) => (
            <li key={r.applicationId}>
              <button
                type="button"
                onClick={() => onOpenRole(item.id, r.applicationId, r.roleStatus)}
                className="w-full text-left text-sm text-ink-600 hover:text-brand-600 font-semibold px-2 py-1.5 rounded-lg hover:bg-brand-50 transition-colors flex items-center justify-between gap-2"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-ink-400 shrink-0">•</span>
                  <span className="truncate">{r.jobTitle}</span>
                </span>
                {showRoleStatus && r.roleStatus ? (
                  <span
                    className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded shrink-0 ${roleStatusBadgeClass(r.roleStatus)}`}
                  >
                    {dashboardStatusLabel(r.roleStatus)}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function HRDash({
  jobs,
  reattemptPendingCount,
  reschedulePendingCount,
  onView,
  onInterview,
  onAnalysis,
  onCvAnalyser,
  onJobs,
  onScreen,
  onTalentPool,
  onAuditLog,
  onReattempts,
  onReschedules,
  onReschedule,
  onLogout,
}) {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const toast = useToast();
  // Resolve initial state once from the URL (shareable filters) then the saved
  // snapshot (Back/refresh restore) then defaults. Computed lazily so it reads
  // the URL/snapshot exactly once on mount.
  const [initState] = useState(() =>
    computeInitialDashState(
      new URLSearchParams(window.location.search),
      readDashSnapshot(),
    ),
  );
  const [filter, setFilter] = useState(initState.filter);
  const [roleScope, setRoleScope] = useState(initState.roleScope);
  const [jobFilter, setJobFilter] = useState(initState.jobFilter); // "" = all positions
  const [search, setSearch] = useState(initState.search);
  const [cvsOpen, setCvsOpen] = useState(false);
  // Seed the debounced value too, so the first data load uses the restored
  // search and the reset effect below doesn't fire on mount and wipe the page.
  const [searchDebounced, setSearchDebounced] = useState(initState.search);
  const [page, setPage] = useState(initState.page);
  const [cursorStack, setCursorStack] = useState(initState.cursorStack);
  const [nextCursor, setNextCursor] = useState(initState.nextCursor);
  const [stats, setStats] = useState(null);
  const [list, setList] = useState({ candidates: [], total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [talentPoolTotal, setTalentPoolTotal] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingStatus, setExportingStatus] = useState(false);
  const [inProcessExpanded, setInProcessExpanded] = useState(initState.inProcessExpanded);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    fetchTalentPoolStats()
      .then((s) => setTalentPoolTotal(s.total ?? 0))
      .catch(() => setTalentPoolTotal(null));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Reset pagination when the user changes a filter/search — but NOT on the
  // initial mount, otherwise a restored page (from the snapshot above) would be
  // immediately reset back to page 1.
  const skipResetOnMountRef = useRef(true);
  useEffect(() => {
    if (skipResetOnMountRef.current) {
      skipResetOnMountRef.current = false;
      return;
    }
    setPage(1);
    setCursorStack([]);
    setNextCursor(null);
  }, [filter, searchDebounced, roleScope, jobFilter]);

  // Persist the navigation state on every change so returning from a candidate
  // detail (via the app Back button, the browser Back button, or a refresh)
  // restores the same filters and page.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        DASH_STATE_KEY,
        JSON.stringify({
          filter,
          roleScope,
          jobFilter,
          search,
          page,
          cursorStack,
          nextCursor,
          inProcessExpanded,
        }),
      );
    } catch (_) {
      /* storage unavailable — non-fatal, restore just won't happen */
    }
  }, [
    filter,
    roleScope,
    jobFilter,
    search,
    page,
    cursorStack,
    nextCursor,
    inProcessExpanded,
  ]);

  // Mirror the active filters into the /hr URL query string so the view is
  // shareable/deep-linkable (e.g. /hr?status=INTERVIEWED&job=j-123&q=anush).
  // replace:true keeps this out of the history stack, so each filter tweak
  // doesn't add a Back step. Page/cursor are intentionally NOT mirrored — they
  // live in the snapshot (a cursor chain can't be reconstructed from a URL).
  useEffect(() => {
    const next = new URLSearchParams();
    if (filter && filter !== "ALL") next.set("status", filter);
    if (roleScope && roleScope !== "all") next.set("scope", roleScope);
    if (jobFilter) next.set("job", jobFilter);
    if (searchDebounced) next.set("q", searchDebounced);
    setSearchParams(next, { replace: true });
  }, [filter, roleScope, jobFilter, searchDebounced, setSearchParams]);

  const loadStats = useCallback(async () => {
    try {
      const s = await fetchCandidateStats({ applicationScope: roleScope });
      setStats(s);
    } catch (e) {
      console.error(e);
    }
  }, [roleScope]);

  useEffect(() => {
    if (location.pathname !== "/hr") return;
    void loadStats();
  }, [location.pathname, loadStats]);

  useEffect(() => {
    if (location.pathname !== "/hr") return undefined;
    const onFocus = () => {
      void loadStats();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [location.pathname, loadStats]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const cursor =
        page > 1 && cursorStack.length >= page - 1
          ? cursorStack[page - 2]
          : undefined;
      const out = await fetchCandidatesPage({
        page,
        limit: PAGE_SIZE,
        cursor,
        status: filter === "ALL" ? undefined : filter,
        search: searchDebounced || undefined,
        applicationScope: roleScope,
        jobId: jobFilter || undefined,
      });
      setList(out);
      setNextCursor(out.nextCursor || null);
      void loadStats();
    } catch (e) {
      console.error(e);
      setListError("Could not load candidates.");
      setList({ candidates: [], total: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [page, filter, searchDebounced, cursorStack, loadStats, roleScope, jobFilter]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  // Live updates: when the server pushes an interview/reattempt/reschedule event,
  // refresh the counts and the candidate list so a just-finished candidate moves
  // buckets without a manual reload. Declared after loadPage so its reference is
  // never in the temporal dead zone during render.
  useEffect(() => {
    if (location.pathname !== "/hr") return undefined;
    const onRealtime = () => {
      void loadStats();
      void loadPage();
    };
    window.addEventListener("swarhr:realtime", onRealtime);
    return () => window.removeEventListener("swarhr:realtime", onRealtime);
  }, [location.pathname, loadStats, loadPage]);

  // Top-level cards: Total, Registered, Applied, [Interview In Process umbrella],
  // Interviewed, Shortlisted, Rejected. The umbrella groups 5 sub-stages that are
  // still individually exclusive — the sum still always equals Total.
  const statCards = stats
    ? [
        { l: "Total", v: stats.total, s: "ALL" },
        { l: "Registered", v: stats.notApplied, s: "NOT_APPLIED" },
        { l: "Applied", v: stats.applied, s: "APPLIED" },
      ]
    : [
        { l: "Total", v: "—", s: "ALL" },
        { l: "Registered", v: "—", s: "NOT_APPLIED" },
        { l: "Applied", v: "—", s: "APPLIED" },
      ];
  const statCardsAfterInProcess = stats
    ? [
        { l: "Interviewed", v: stats.interviewed, s: "INTERVIEWED" },
        { l: "Shortlisted", v: stats.shortlisted, s: "SHORTLISTED" },
        { l: "Rejected", v: stats.rejected, s: "REJECTED" },
      ]
    : [
        { l: "Interviewed", v: "—", s: "INTERVIEWED" },
        { l: "Shortlisted", v: "—", s: "SHORTLISTED" },
        { l: "Rejected", v: "—", s: "REJECTED" },
      ];
  const inProcessSubCards = stats
    ? [
        { l: "Scheduled", v: stats.scheduled, s: "SCHEDULED" },
        { l: "Rescheduled", v: stats.rescheduled, s: "RESCHEDULED" },
        { l: "Reattempt", v: stats.reattempt, s: "REATTMPT" },
        { l: "Incomplete", v: stats.incomplete, s: "INCOMPLETE" },
        { l: "Missed", v: stats.missed, s: "MISSED" },
      ]
    : [
        { l: "Scheduled", v: "—", s: "SCHEDULED" },
        { l: "Rescheduled", v: "—", s: "RESCHEDULED" },
        { l: "Reattempt", v: "—", s: "REATTMPT" },
        { l: "Incomplete", v: "—", s: "INCOMPLETE" },
        { l: "Missed", v: "—", s: "MISSED" },
      ];
  const inProcessTotal = stats
    ? (stats.scheduled ?? 0) +
      (stats.rescheduled ?? 0) +
      (stats.reattempt ?? 0) +
      (stats.incomplete ?? 0) +
      (stats.missed ?? 0)
    : "—";
  const inProcessActive = IN_PROCESS_STAGES.includes(filter);

  const jt = (id) => jobs.find((j) => j.id === id)?.title || "—";
  const schedFor = (c) =>
    c.interviewScheduledAt ||
    getLatestAppForJob(c.applicationHistory, c.jobId)?.interviewScheduledAt;
  const filtered = list.candidates || [];
  const totalPages = list.totalPages || 1;
  const isGroupedView =
    filter === "APPLIED" ||
    filter === "SHORTLISTED" ||
    filter === "REJECTED" ||
    filter === "POTENTIAL" ||
    filter === "INTERVIEWED" ||
    filter === "REATTMPT" ||
    filter === "RESCHEDULED" ||
    (roleScope === "multiple" && filter === "ALL");
  const handleSelectFilter = (s) => {
    setFilter(s);
    if (IN_PROCESS_STAGES.includes(s)) setInProcessExpanded(true);
  };
  const handleToggleRepeatApplicants = () => {
    setRoleScope((prev) => (prev === "multiple" ? "all" : "multiple"));
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportCandidateReport({
        status: filter === "ALL" ? undefined : filter,
        search: searchDebounced || undefined,
        activeJobOnly: false,
        applicationScope: roleScope,
      });
      toast.show({ tone: "success", title: "Export complete", message: "Candidate report CSV downloaded." });
    } catch (e) {
      console.error(e);
      toast.show({ tone: "danger", title: "Export failed", message: "Could not export candidate report. Please try again." });
    } finally {
      setExporting(false);
    }
  };

  const handleExportStatus = async () => {
    setExportingStatus(true);
    try {
      await exportCandidateStatusReport({ applicationScope: roleScope });
      toast.show({ tone: "success", title: "Export complete", message: "Candidate status CSV downloaded." });
    } catch (e) {
      console.error(e);
      toast.show({ tone: "danger", title: "Export failed", message: "Could not export candidate status. Please try again." });
    } finally {
      setExportingStatus(false);
    }
  };

  return (
    <div className="min-h-dvh safe-bottom bg-surface-alt">
      <div className="bg-white border-b border-line text-ink-900 px-4 sm:px-6 py-3 sm:py-4 safe-top safe-x flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center justify-between gap-3 w-full md:w-auto">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center text-white font-black text-sm">
              HR
            </div>
            <span className="font-bold">HR Portal</span>
            <Badge />
          </div>
          <div className="flex md:hidden items-center gap-2">
            <button
              type="button"
              onClick={() => setNavOpen((v) => !v)}
              className="px-3 py-1.5 bg-surface-alt hover:bg-slate-100 text-ink-700 border border-line rounded-lg text-sm font-medium transition-colors"
              aria-expanded={navOpen}
              aria-label="Toggle navigation menu"
            >
              {navOpen ? "✕ Close" : "☰ Menu"}
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold transition-colors inline-flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
              Logout
            </button>
          </div>
        </div>
        <div className={`${navOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row gap-2 w-full md:w-auto pb-1 md:pb-0`}>
          <button
            type="button"
            onClick={() => { onCvAnalyser(); setNavOpen(false); }}
            className="w-full md:w-auto px-3 py-1.5 bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 rounded-lg text-sm font-medium transition-colors text-left md:text-center"
          >
            CV Analyser
          </button>
          <button
            type="button"
            onClick={() => { setCvsOpen(true); setNavOpen(false); }}
            className="w-full md:w-auto px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg text-sm font-medium transition-colors text-left md:text-center"
          >
            CVS
          </button>
          <button
            onClick={() => { onJobs(); setNavOpen(false); }}
            className="w-full md:w-auto px-3 py-1.5 bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded-lg text-sm font-medium transition-colors text-left md:text-center"
          >
            Jobs
          </button>
          <button
            onClick={() => { onTalentPool(); setNavOpen(false); }}
            className="w-full md:w-auto px-3 py-1.5 bg-brand-50 hover:bg-brand-100 text-brand-700 border border-brand-200 rounded-lg text-sm font-medium transition-colors text-left md:text-center"
          >
            🌟 Talent Pool ({talentPoolTotal ?? "…"})
          </button>
          <button
            type="button"
            onClick={() => { onReattempts(); setNavOpen(false); }}
            className="w-full md:w-auto px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-lg text-sm font-medium transition-colors relative text-left md:text-center"
          >
            Reattempts
            {typeof reattemptPendingCount === "number" &&
            reattemptPendingCount > 0 ? (
              <span className="absolute -top-1 -right-1 min-w-[1.1rem] h-[1.1rem] px-1 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center">
                {reattemptPendingCount > 9 ? "9+" : reattemptPendingCount}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            onClick={() => { onReschedules(); setNavOpen(false); }}
            className="w-full md:w-auto px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-lg text-sm font-medium transition-colors relative text-left md:text-center"
          >
            Reschedule
            {typeof reschedulePendingCount === "number" &&
            reschedulePendingCount > 0 ? (
              <span className="absolute -top-1 -right-1 min-w-[1.1rem] h-[1.1rem] px-1 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center">
                {reschedulePendingCount > 9 ? "9+" : reschedulePendingCount}
              </span>
            ) : null}
          </button>
          <button
            onClick={() => { onAuditLog(); setNavOpen(false); }}
            className="w-full md:w-auto px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg text-sm font-medium transition-colors text-left md:text-center"
          >
            📋 Audit
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="hidden md:inline-flex px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold transition-colors items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            Logout
          </button>
        </div>
      </div>
      <div className="max-w-[90rem] mx-auto px-4 sm:px-6 py-6 sm:py-8 safe-x">
        <div className="flex items-center flex-wrap gap-4 mb-6">
          <h1 className="text-xl sm:text-3xl font-black text-ink-900">
            Recruitment Dashboard
          </h1>
        </div>
        <div className="mb-2">
          <div className="grid grid-cols-2 gap-2 md:flex md:overflow-x-auto hide-scrollbar md:-mx-1 md:px-1 md:items-stretch md:gap-2 md:min-w-0">
            {statCards.map((s) => (
              <button
                key={s.l}
                onClick={() => handleSelectFilter(s.s)}
                className={`md:flex-1 text-left bg-white rounded-xl p-3 sm:p-4 border shadow-sm transition-all hover:shadow-md ${filter === s.s ? "border-brand-400 ring-2 ring-brand-100" : "border-line"}`}
              >
                <p className="text-[10px] sm:text-xs font-bold text-ink-400 uppercase tracking-wider mb-1 truncate">
                  {s.l}
                </p>
                <p className="text-xl sm:text-2xl font-black text-ink-900">{s.v}</p>
              </button>
            ))}
            <button
              onClick={() => setInProcessExpanded((v) => !v)}
              className={`md:flex-1 text-left bg-white rounded-xl p-3 sm:p-4 border shadow-sm transition-all hover:shadow-md ${inProcessActive ? "border-brand-400 ring-2 ring-brand-100" : "border-line"}`}
            >
              <p className="text-[10px] sm:text-xs font-bold text-ink-400 uppercase tracking-wider mb-1 truncate flex items-center gap-1">
                Interview In Process
                <span className="text-ink-400">{inProcessExpanded ? "▲" : "▼"}</span>
              </p>
              <p className="text-xl sm:text-2xl font-black text-ink-900">{inProcessTotal}</p>
            </button>
            {statCardsAfterInProcess.map((s) => (
              <button
                key={s.l}
                onClick={() => handleSelectFilter(s.s)}
                className={`md:flex-1 text-left bg-white rounded-xl p-3 sm:p-4 border shadow-sm transition-all hover:shadow-md ${filter === s.s ? "border-brand-400 ring-2 ring-brand-100" : "border-line"}`}
              >
                <p className="text-[10px] sm:text-xs font-bold text-ink-400 uppercase tracking-wider mb-1 truncate">
                  {s.l}
                </p>
                <p className="text-xl sm:text-2xl font-black text-ink-900">{s.v}</p>
              </button>
            ))}
            <button
              onClick={() => handleSelectFilter("POTENTIAL")}
              title="Candidates HR marked as potential for later consideration"
              className={`md:flex-1 text-left bg-white rounded-xl p-3 sm:p-4 border-2 shadow-sm transition-all hover:shadow-md ${filter === "POTENTIAL" ? "border-indigo-400 ring-2 ring-indigo-100" : "border-line"}`}
            >
              <p className="text-[10px] sm:text-xs font-bold text-indigo-600 uppercase tracking-wider mb-1 truncate">
                Potential Candidate
              </p>
              <p className="text-xl sm:text-2xl font-black text-ink-900">
                {stats ? stats.potential ?? 0 : "—"}
              </p>
            </button>
            <button
              onClick={handleToggleRepeatApplicants}
              title="Candidates who have applied to more than one vacancy — not part of the Total tally"
              className={`md:flex-1 text-left bg-white rounded-xl p-3 sm:p-4 border-2 border-dashed shadow-sm transition-all hover:shadow-md ${roleScope === "multiple" ? "border-amber-400 ring-2 ring-amber-100" : "border-line"}`}
            >
              <p className="text-[10px] sm:text-xs font-bold text-amber-600 uppercase tracking-wider mb-1 truncate">
                Repeat Applicants
              </p>
              <p className="text-xl sm:text-2xl font-black text-ink-900">
                {stats ? stats.repeatApplicants : "—"}
              </p>
            </button>
          </div>
          {inProcessExpanded ? (
            <div className="grid grid-cols-2 gap-2 mt-2 md:flex md:items-stretch md:gap-2 md:min-w-[1120px]">
              <div className="hidden md:block flex-[3]" />
              {inProcessSubCards.map((s) => (
                <button
                  key={s.l}
                  onClick={() => setFilter(s.s)}
                  className={`md:flex-1 text-left bg-surface-alt rounded-xl p-2.5 sm:p-3 border shadow-sm transition-all hover:shadow-md ${filter === s.s ? "border-brand-400 ring-2 ring-brand-100" : "border-line"}`}
                >
                  <p className="text-[10px] font-bold text-ink-400 uppercase tracking-wider mb-1 truncate">
                    {s.l}
                  </p>
                  <p className="text-lg font-black text-ink-900">{s.v}</p>
                </button>
              ))}
              <div className="hidden md:block flex-[4]" />
            </div>
          ) : null}
        </div>
        <div className="mb-4" />
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-3">
          <h2 className="text-lg sm:text-xl font-bold text-ink-900">
            Candidates{" "}
            {filter !== "ALL" && (
              <span className="text-sm text-ink-400 font-normal">
                · {dashboardStatusLabel(filter)}
              </span>
            )}
            {jobFilter && (
              <span className="text-sm text-brand-600 font-semibold ml-2">
                · {jt(jobFilter)}
              </span>
            )}
            {list.total > 0 && (
              <span className="text-sm text-ink-400 font-normal ml-2">
                ({list.total} total)
              </span>
            )}
          </h2>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 w-full lg:w-auto">
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or email…"
              className="w-full sm:w-56"
            />
            <select
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
              aria-label="Filter by position"
              title="Filter candidates by job position"
              className="h-9 w-full sm:w-auto sm:max-w-[13rem] rounded-lg border border-line bg-white px-2 text-sm text-ink-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
            >
              <option value="">All positions</option>
              {(jobs || []).map((j) => (
                <option key={j.id} value={j.id}>
                  {j.title}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              onClick={() => void handleExportStatus()}
              loading={exportingStatus}
              className="w-full sm:w-auto whitespace-nowrap"
            >
              Export candidate status
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => void handleExport()}
              loading={exporting}
              className="w-full sm:w-auto whitespace-nowrap"
            >
              Export Candidate details
            </Button>
          </div>
        </div>
        {filter !== "ALL" ||
        jobFilter ||
        roleScope === "multiple" ||
        search.trim() ? (
          <div className="flex items-center flex-wrap gap-2 mb-4 -mt-1">
            <span className="text-xs font-semibold text-ink-400">Filters:</span>
            {filter !== "ALL" ? (
              <FilterChip
                label={`Status: ${dashboardStatusLabel(filter)}`}
                onRemove={() => setFilter("ALL")}
              />
            ) : null}
            {jobFilter ? (
              <FilterChip
                label={`Position: ${jt(jobFilter)}`}
                onRemove={() => setJobFilter("")}
              />
            ) : null}
            {search.trim() ? (
              <FilterChip
                label={`Search: "${search.trim()}"`}
                onRemove={() => setSearch("")}
              />
            ) : null}
            {roleScope === "multiple" ? (
              <FilterChip
                label="Repeat applicants"
                onRemove={() => setRoleScope("all")}
              />
            ) : null}
            <button
              type="button"
              onClick={() => {
                setFilter("ALL");
                setJobFilter("");
                setSearch("");
                setRoleScope("all");
              }}
              className="text-xs font-bold text-brand-600 hover:text-brand-700 ml-1"
            >
              Clear all
            </button>
          </div>
        ) : null}
        {listError ? (
          <div className="bg-red-50 text-red-700 rounded-xl p-4 text-sm mb-4">
            {listError}
          </div>
        ) : null}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-line p-5">
                <div className="flex items-center gap-3 mb-4">
                  <Skeleton className="w-11 h-11 rounded-xl" />
                  <div className="flex-1">
                    <Skeleton className="h-3 w-24 mb-2" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
                <Skeleton className="h-3 w-40 mb-2" />
                <Skeleton className="h-8 w-full rounded-lg mt-4" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border-2 border-dashed border-line py-16 text-center">
            <p className="text-ink-400">No candidates on this page.</p>
          </div>
        ) : isGroupedView ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((item) => (
              <DecisionCard
                key={item.id}
                item={item}
                showRoleStatus={roleScope === "multiple" && filter === "ALL"}
                onOpenRole={(id, applicationId, roleStatus) => {
                  const status = roleStatus || filter;
                  if (
                    status === "SHORTLISTED" ||
                    status === "REJECTED" ||
                    status === "POTENTIAL" ||
                    status === "INTERVIEWED"
                  ) {
                    onAnalysis(id, applicationId);
                  } else {
                    onView(id);
                  }
                }}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((c) => (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                aria-label={`View ${c.name}`}
                className="bg-white rounded-2xl border border-line p-5 hover:shadow-lg hover:border-brand-200 transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                onClick={() => onView(c.id)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onView(c.id); } }}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600 font-black text-lg shrink-0">
                      {c.name[0]}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-ink-900 truncate" title={c.name}>{c.name}</p>
                      <p className="text-xs text-ink-400 truncate" title={c.email}>{c.email}</p>
                      {c.phone && <p className="text-xs text-ink-400 truncate">{c.phone}</p>}
                    </div>
                  </div>
                  <span
                    className={`text-xs font-black uppercase px-2 py-1 rounded-lg shrink-0 whitespace-nowrap ${SB[c.status] || "bg-slate-100"}`}
                  >
                    {dashboardStatusLabel(c.status)}
                  </span>
                </div>
                <p className="text-sm font-semibold text-ink-600 mb-1">
                  {c.jobId ? jt(c.jobId) : "—"}
                  {c.fromTalentPool ? (
                    <span className="text-xs font-semibold text-amber-700 ml-1.5">
                      *talent pool
                    </span>
                  ) : null}
                </p>
                {schedFor(c) ? (
                  <p className="text-xs font-bold text-teal-700 mb-2">
                    📅 Interview scheduled: {fmtDateTime(schedFor(c))}
                  </p>
                ) : null}
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <div
                    className={`text-xs font-bold px-2 py-1 rounded-lg ${c.consent ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}
                  >
                    {c.consent ? "✓ Consent" : "⚠ No consent"}
                  </div>
                  {candidateHasAnyInterviewTranscript(c) && c.status !== "INCOMPLETE" && (
                    <div className="text-xs font-bold px-2 py-1 rounded-lg bg-teal-50 text-teal-600">
                      🎤 Done
                    </div>
                  )}
                  {(c.applicationCount > 1 ||
                    (c.applicationHistory || []).length > 1) && (
                    <div className="text-xs font-bold px-2 py-1 rounded-lg bg-slate-100 text-ink-600">
                      {c.applicationCount ||
                        (c.applicationHistory || []).length}{" "}
                      apps
                    </div>
                  )}
                </div>
                <div
                  className="flex gap-2 pt-3 border-t border-line"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    size="sm"
                    onClick={() => onView(c.id)}
                    className="flex-1"
                  >
                    View
                  </Button>
                  {(c.status === "INTERVIEWED" ||
                    c.analysis ||
                    c.hasAnalysis ||
                    candidateHasAnyInterviewTranscript(c) ||
                    (c.applicationHistory || []).some(
                      (a) => a.analysis || a.interviewCompletedAt,
                    )) && (
                    <button
                      onClick={() => onAnalysis(c.id)}
                      className="flex-1 py-2 bg-teal-600 text-white text-xs font-bold rounded-lg"
                    >
                      Analysis
                    </button>
                  )}
                  {typeof onReschedule === "function" &&
                    c.consent &&
                    schedFor(c) &&
                    interviewStartSlotStatus(schedFor(c), false).tooLate &&
                    !c.interviewCompletedAt &&
                    !candidateHasAnyInterviewTranscript(c) && (
                      <button
                        onClick={() => onReschedule(c.id)}
                        className="flex-1 py-2 bg-rose-600 text-white text-xs font-bold rounded-lg"
                      >
                        Reschedule
                      </button>
                    )}
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading &&
          (page > 1 || nextCursor || (isGroupedView && page < totalPages)) && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              type="button"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Previous
            </Button>
            <span className="text-sm text-ink-600">
              Page {page}
              {list.total != null ? ` · ${list.total} total` : ""}
            </span>
            <Button
              type="button"
              variant="secondary"
              disabled={
                isGroupedView ? page >= totalPages : !nextCursor
              }
              onClick={() => {
                if (!isGroupedView && nextCursor) {
                  setCursorStack((s) => {
                    const copy = [...s];
                    copy[page - 1] = nextCursor;
                    return copy;
                  });
                }
                setPage((p) => p + 1);
              }}
            >
              Next →
            </Button>
          </div>
        )}
      </div>
      {cvsOpen ? <CvsStatsModal onClose={() => setCvsOpen(false)} /> : null}
    </div>
  );
}
